import { ExclamationCircleOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { type DragEvent as ReactDragEvent, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import type Ad from 'stores/Ad';
import type AdsManager from 'stores/AdsManager';
import { recycleEqualNullableArray } from 'utils/Hooks';

import AdListTableRow from './AdListTableRow';
import {
  type AdjacentEdge,
  type AdListTableReorderCallback,
  type AdListTableRowCallback,
  type AdListTableRowCheckCallback,
  type InsertionPoint,
  recycleEqualNullableInsertionPoint,
} from './types';

// This component is structured such that the *row* components
// (AdListTableRow) emit drag start and drag end events, and the container
// listens for drag over and drop events. Though this component still hooks
// into row drag start/end events, to determine the initial drag set (start
// emitter + any other checked rows), and also to detect drag ends with no
// valid drop targets (i.e. drag end without dropAccepted.current being set
// to true by an onDrop handler).
//
// The parent component surveys the child row's geometry to find the
// nearest valid insertion pivot. It's best done here because it has the
// full picture - it does *not* consider rows representing dragged items
// (see the `exclude` parameter in `setDragInsertionPoint`). Insertion
// points are tuples of (rowId:string, edge:AdjacentEdge) - the latter
// being either 'before' or 'after'. `reorderElements` resolves these into
// array indices and produces both a reordered Array of entries, and an old
// index->new index mapping Array (used for nudging rows during a drag)
//
// An initial attempt put the drop handling on the rows as well, but it was
// moved up to the parent because of the sibling communication that would
// otherwise be necessary.

interface ContainerProps {
  $disabled: boolean;
}

const Container = styled.div<ContainerProps>`
  width: 500px;
  height: 100%;
  position: relative;

  ${(p) =>
    p.$disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

const TableContainer = styled.div`
  border-style: solid;
  border-color: #e6e6e6;
  border-width: 1px 1px 0 0;
  display: flex;
  flex-grow: 1;
  flex-flow: column;
  background: #fff;
  width: 100%;
  height: 100%;
  overflow: auto;

  .ant-table-thead > tr,
  .ant-table-tbody > tr {
    cursor: pointer;
  }

  .ant-table-small {
    border-radius: 0px;
  }

  .ant-table-selection-column {
    display: none;
  }
`;

const Notification = styled.div`
  background-color: #fffbe6;
  border-bottom: 1px solid #ffe58f;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e9e9e9;
  padding: 5px 3px 5px 8px;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;

  .anticon {
    font-size: 19px;
    color: #f0d374;
    margin-right: 8px;
  }
`;

const RowContainer = styled.div`
  position: relative;

  .ant-input {
    height: 28px;
    position: relative;
    top: 0px;
    margin-top: -1px;
    margin-bottom: 9px;
  }

  .ant-tag,
  .ant-tag a,
  .ant-tag {
    padding: 3px 9px;
    font-size: 13px;
    margin-bottom: 8px;
  }
`;

export interface AdListTableProps {
  ads?: AdsManager;
  allowReorder?: boolean;
  disabled?: boolean;
  onCheck?: AdListTableRowCheckCallback;
  onClick?: AdListTableRowCallback;
  onReorder?: AdListTableReorderCallback;
}

interface Identified {
  id: string;
}

/**
 * The result of `reorderElements`.
 */
interface ReorderResult<T> {
  /**
   * The members of the input `fullSet` in the new order.
   */
  reorderedSet: T[];

  /**
   * A lookup of old indices to their new positions. For example, if
   * `.oldToNewIndices[1] === 6`, that expresses that the element at index
   * 1 in the *input* Array appears at index 6 in `reorderedSet`.
   */
  oldToNewIndices: number[];
}

/**
 * Reorders an array by nominating that a subset of its elements should be
 * removed and reinserted at the given insertionPoint.
 *
 * `moveSubset` must only ever contain elements that also exist in
 * `fullSet`.
 *
 * `insertionPoint` must specify an `id` property that appears in
 * `fullSet`. If it is not found, the result is a no-op.
 */
function reorderElements<T extends Identified>(
  fullSet: ReadonlyArray<T>,
  moveSubset: ReadonlyArray<T>,
  insertionPoint: InsertionPoint
): ReorderResult<T> {
  let insertionIndex = fullSet.findIndex((el) => el.id === insertionPoint.id);
  if (insertionIndex === -1) {
    console.warn(`reorderElements: insertionPoint.id not found in fullSet`);
    return {
      reorderedSet: fullSet.slice(),
      oldToNewIndices: fullSet.map((_, i) => i),
    };
  }

  if (insertionPoint.edge === 'after') {
    insertionIndex++;
  }

  const lookup = new Map(
    fullSet.map((element) => [
      element.id,
      { element, newIndex: -1, move: false },
    ])
  );

  for (const moveEl of moveSubset) {
    const entry = lookup.get(moveEl.id);
    if (entry) {
      entry.move = true;
    } else {
      console.warn(
        `reorderElements: Not a subset: Element with id ${moveEl.id} not found in fullSet`
      );
    }
  }

  const reorderedSet: T[] = [];
  for (let i = 0; i < insertionIndex; i++) {
    const el = fullSet[i];
    const entry = lookup.get(el.id);
    if (entry && !entry.move) {
      entry.newIndex = reorderedSet.length;
      reorderedSet.push(el);
    }
  }

  for (const moveEl of moveSubset) {
    const entry = lookup.get(moveEl.id);
    if (entry) {
      entry.newIndex = reorderedSet.length;
      reorderedSet.push(moveEl);
    }
  }

  for (let j = insertionIndex; j < fullSet.length; j++) {
    const el = fullSet[j];
    const entry = lookup.get(el.id);
    if (entry && !entry.move) {
      entry.newIndex = reorderedSet.length;
      reorderedSet.push(el);
    }
  }

  const oldToNewIndices = fullSet.map(
    (el) => lookup.get(el.id)?.newIndex ?? NaN
  );
  return { reorderedSet, oldToNewIndices };
}

interface RowGeometry {
  id: string;
  rect: DOMRect;
}

/**
 * Converts a row's viewport rect into coordinates that survive scrolling,
 * by folding in the scroll offset at the moment of measurement.
 *
 * This is what lets geometry be measured once and reused for a whole drag:
 * `searchForInsertionPoint` adds the *current* scroll offset to the pointer,
 * so both end up in the same space even if the list scrolls in between.
 */
function getScrollCompensatedRect(
  element: HTMLElement,
  scrollParent: HTMLElement
) {
  const clientRect = element.getBoundingClientRect();
  return new DOMRect(
    clientRect.left + scrollParent.scrollLeft,
    clientRect.top + scrollParent.scrollTop,
    clientRect.width,
    clientRect.height
  );
}

/**
 * Measures every row in one pass. Call this at drag start, *before* any row
 * has been displaced: rows carry a translateY while a drag is in progress
 * and getBoundingClientRect reports the transformed box, so measuring later
 * would compare the pointer against nudged geometry.
 *
 * The snapshot is fixed for the duration of a drag, so rows arriving from a
 * Firestore snapshot mid-drag are not considered as insertion candidates
 * until the next one begins. That is a deliberate trade for stable geometry.
 */
function snapshotRowGeometry(scrollParent: HTMLElement): RowGeometry[] {
  const rowElements = scrollParent.querySelectorAll<HTMLElement>(
    '[data-adhub-adlisttablerow]'
  );

  return Array.from(rowElements).flatMap((element) => {
    const id = element.dataset.adhubAdlisttablerow;
    if (!id) {
      console.warn('[AdListTable#snapshotRowGeometry]: no adId', element);
      return [];
    }
    return [{ id, rect: getScrollCompensatedRect(element, scrollParent) }];
  });
}

function searchForInsertionPoint(
  rows: ReadonlyArray<RowGeometry>,
  scrollParent: HTMLElement,
  clientY: number,
  exclude: ReadonlySet<string> | null
): InsertionPoint | null {
  const y = scrollParent.scrollTop + clientY;

  let bestDistance = Infinity;
  let bestId: string | undefined;
  // won't be relevant if not found, so we can dispense with |undefined and such
  let bestEdge: AdjacentEdge = 'before';

  for (const geometry of rows) {
    if (exclude?.has(geometry.id)) continue;

    const yCenter = geometry.rect.top + geometry.rect.height / 2;
    const yDelta = Math.abs(y - yCenter);
    if (yDelta < bestDistance) {
      bestDistance = yDelta;
      bestId = geometry.id;
      bestEdge = y < yCenter ? 'before' : 'after';
    }
  }

  if (bestId == null) return null;

  return {
    id: bestId,
    edge: bestEdge,
  };
}

function AdListTable({
  ads,
  allowReorder,
  disabled,
  onCheck,
  onClick,
  onReorder,
}: AdListTableProps) {
  // if allowReorder is not explicitly set, assume true if an onReorder
  // callback is provided
  if (allowReorder == null) allowReorder = !!onReorder;

  const adSet = ads?.mounted && ads.filtered;
  const filteringIsActive = ads?.filtering;

  const trulyAllowReorder =
    allowReorder &&
    !!onReorder &&
    !disabled &&
    !filteringIsActive &&
    !!adSet &&
    adSet.length > 1;

  const [dragSet, setDragSet] = useState<ReadonlyArray<Ad> | null>(null);
  const [dragInsertionPoint, setDragInsertionPoint] =
    useState<InsertionPoint | null>(null);

  const dropAccepted = useRef(false);

  const rowGeometryBasisRef = useRef<HTMLDivElement>(null);

  // Populated at drag start and held for the duration of the drag; see
  // snapshotRowGeometry. Deliberately not maintained per-row via a ref
  // callback: a callback recreated each render re-attaches on every row,
  // which both defeats observer's memo bailout and reintroduces mid-drag
  // re-measurement.
  const rowGeometry = useRef<ReadonlyArray<RowGeometry>>([]);

  const dragSetIds = useMemo(
    () => new Set(dragSet?.map((ad) => ad.id)),
    [dragSet]
  );

  const onDragStart: AdListTableRowCallback = ({ ad }) => {
    dropAccepted.current = false;

    const rowGeometryBasis = rowGeometryBasisRef.current;
    rowGeometry.current = rowGeometryBasis
      ? snapshotRowGeometry(rowGeometryBasis)
      : [];

    const newDragSet = (adSet || []).filter(
      (item) => item.selected || item.id === ad.id
    );

    setDragSet((oldDragSet) =>
      recycleEqualNullableArray(oldDragSet, newDragSet)
    );

    setDragInsertionPoint((oldInsertionPoint) =>
      recycleEqualNullableInsertionPoint(oldInsertionPoint, {
        id: ad.id,
        edge: 'before',
      })
    );
  };

  // `dragSet` is only non-null between a row's dragstart and its dragend, so
  // it doubles as "this drag is one of ours". Used to avoid claiming file
  // drags: those belong to the dragHelper in Creative.tsx, and accepting them
  // here would show a move cursor over the ad list during an asset upload.
  const isOwnDrag = dragSet != null;

  const onDragOver = (event: ReactDragEvent<HTMLElement>) => {
    const rowGeometryBasis = rowGeometryBasisRef.current;
    if (!rowGeometryBasis || !isOwnDrag) return;

    // Cancelling dragover is what declares this element a drop target; drop
    // does not fire otherwise. installDragBackstop() in index.tsx happens to
    // cancel dragover window-wide, so this worked without it - but that
    // backstop exists to stop stray file drops navigating away, not to enable
    // our drop targets. Doing it here keeps this component correct on its own
    // terms, and survives the backstop ever being narrowed to file drags.
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    const insertionPoint = searchForInsertionPoint(
      rowGeometry.current,
      rowGeometryBasis,
      event.clientY,
      dragSetIds
    );
    if (insertionPoint) {
      setDragInsertionPoint((oldInsertionPoint) =>
        recycleEqualNullableInsertionPoint(oldInsertionPoint, insertionPoint)
      );
    }
  };

  const onDrop = (event: ReactDragEvent<HTMLElement>) => {
    if (!isOwnDrag) return;

    // Canceled for the same reason as dragover - see there. We only flag a
    // valid drop target here; listening for onDragEnd from the dragged
    // element handles the proper dispatch.
    event.preventDefault();
    dropAccepted.current = true;
  };

  const onDragEnd: AdListTableRowCallback = (_info) => {
    rowGeometry.current = [];

    if (
      dropAccepted.current &&
      onReorder &&
      adSet &&
      dragSet &&
      dragInsertionPoint
    ) {
      const reordering = reorderElements(adSet, dragSet, dragInsertionPoint);
      onReorder({
        ads: reordering.reorderedSet,

        // todo: store an unlock value so calling this will only have an
        //  effect if nothing has changed
        flush: () => {
          setDragSet(null);
          setDragInsertionPoint(null);
        },
      });
      return;
    }

    // otherwise just revert
    setDragSet(null);
    setDragInsertionPoint(null);
  };

  const tentativeReordering = useMemo(
    () =>
      adSet && dragSet && dragInsertionPoint
        ? reorderElements(adSet, dragSet, dragInsertionPoint)
        : null,
    [adSet, dragSet, dragInsertionPoint]
  );

  return (
    <Container $disabled={!!disabled}>
      {adSet && (
        <TableContainer ref={rowGeometryBasisRef}>
          {filteringIsActive && (
            <Notification>
              <ExclamationCircleOutlined /> Filtered Results
            </Notification>
          )}

          <RowContainer onDragOver={onDragOver} onDrop={onDrop}>
            {adSet.map((ad, index) => {
              const isDragSetMember = dragSetIds.has(ad.id);
              const tentativeNewIndex =
                tentativeReordering?.oldToNewIndices[index] ?? index;
              return (
                <AdListTableRow
                  key={ad.id}
                  ad={ad}
                  onCheck={onCheck}
                  onClick={onClick}
                  draggable={trulyAllowReorder}
                  onDragStart={trulyAllowReorder ? onDragStart : undefined}
                  onDragEnd={trulyAllowReorder ? onDragEnd : undefined}
                  isDragSetMember={isDragSetMember}
                  displace={isDragSetMember ? 0 : tentativeNewIndex - index}
                />
              );
            })}
          </RowContainer>
        </TableContainer>
      )}
    </Container>
  );
}

export default observer(AdListTable);
